import type { MedioPagoReserva } from '@padel/domain';
import { nombreDeMedioReserva } from '@/publico/dominio/formato';
/**
 * Resumen previo a confirmar.
 *
 * Es la ultima pantalla antes de que se cree algo, asi que dice **todo** lo que la
 * persona esta por aceptar: cancha, dia, inicio, final, duracion, precio y las
 * condiciones que le van a aplicar.
 *
 * El final se muestra siempre, y cuando el turno cruza la medianoche se dice con
 * palabras en que dia termina. Una reserva es instante + duracion (ADR-007): la de las
 * 23:30 de 90 minutos termina a la 01:00 del dia siguiente, y esconderlo seria un
 * problema real cuando la persona llegue a la cancha.
 */

import * as formato from '@/publico/dominio/formato';
import type { FinDeTurno } from '@/publico/dominio/horario';

interface PropiedadesResumen {
  readonly canchaNombre: string;
  readonly localFecha: string;
  readonly localHora: string;
  readonly fin: FinDeTurno | null;
  readonly duracionMinutos: number;
  readonly precioCentesimos: number;
  readonly medioPago: MedioPagoReserva;
}

export function ResumenReserva({
  canchaNombre,
  localFecha,
  localHora,
  fin,
  duracionMinutos,
  precioCentesimos,
  medioPago,
}: PropiedadesResumen): React.JSX.Element {
  return (
    <div className="resumen">
      <dl className="resumen__datos">
        <div className="resumen__fila">
          <dt>Cancha</dt>
          <dd>{canchaNombre}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Dia</dt>
          <dd>{formato.fechaLarga(localFecha)}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Empieza</dt>
          <dd>{localHora}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Termina</dt>
          <dd>
            {fin === null ? '—' : fin.localHora}
            {fin?.cruzaMedianoche === true ? (
              <span className="resumen__nota">{` del ${formato.fechaLarga(fin.localFecha)}`}</span>
            ) : null}
          </dd>
        </div>
        <div className="resumen__fila">
          <dt>Duracion</dt>
          <dd>{formato.duracion(duracionMinutos)}</dd>
        </div>
        <div className="resumen__fila resumen__fila--precio">
          <dt>Precio</dt>
          <dd>{formato.precio(precioCentesimos)}</dd>
        </div>
        <div className="resumen__fila">
          <dt>Método de pago</dt>
          <dd>{nombreDeMedioReserva(medioPago)}</dd>
        </div>
      </dl>

      {fin?.cruzaMedianoche === true ? (
        <p className="nota">
          Este turno cruza la medianoche: empieza el {formato.fechaLarga(localFecha)} y termina el{' '}
          {formato.fechaLarga(fin.localFecha)}.
        </p>
      ) : null}

      <section aria-labelledby="condiciones-titulo" className="condiciones">
        <h3 className="condiciones__titulo" id="condiciones-titulo">
          Condiciones
        </h3>
        <ul>
          <li>La reserva queda registrada automáticamente al confirmarla.</li>
          <li>Para cancelar, tenés que comunicarte con el complejo.</li>
          <li>
            Si el cliente cancela una reserva confirmada, se cobrará un cargo de{' '}
            <strong>$250</strong>.
          </li>
          <li>
            Si el complejo cancela el turno por lluvia, mantenimiento u otra causa,{' '}
            <strong>no se aplicará ningún cargo.</strong>
          </li>
        </ul>
      </section>
    </div>
  );
}
